import type { Community, CommunityKind } from '@/types';

export type SwitcherCommunity = Pick<
  Community,
  'id' | 'name' | 'logo' | 'kind' | 'parentCommunityId'
> & {
  navigationConfig?: Community['navigationConfig'];
  isProjectsEnabled?: boolean;
  isBusinessDirectoryEnabled?: boolean;
  status?: string;
};

export interface MembershipTreeNode {
  community: SwitcherCommunity;
  children: MembershipTreeNode[];
}

export type SmartSwitchResult = { action: 'navigate'; communityId: string } | { action: 'expand' };

function isAggregator(kind: CommunityKind | string | null | undefined): boolean {
  return kind === 'AGGREGATOR';
}

/** Communities nested by parentCommunityId among memberships only. */
export function buildMembershipForest(communities: SwitcherCommunity[]): MembershipTreeNode[] {
  const byId = new Map<string, MembershipTreeNode>();
  for (const community of communities) {
    byId.set(community.id, { community, children: [] });
  }

  const roots: MembershipTreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.community.parentCommunityId;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortRec = (nodes: MembershipTreeNode[]) => {
    nodes.sort((a, b) => a.community.name.localeCompare(b.community.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

/** Leaf memberships: non-aggregators, or aggregators with no children in this forest. */
export function getLeafCommunities(node: MembershipTreeNode): SwitcherCommunity[] {
  if (node.children.length === 0) {
    return [node.community];
  }

  if (!isAggregator(node.community.kind)) {
    const fromChildren = node.children.flatMap((child) => getLeafCommunities(child));
    return [node.community, ...fromChildren];
  }

  return node.children.flatMap((child) => getLeafCommunities(child));
}

export function collectLeafIds(node: MembershipTreeNode): string[] {
  return getLeafCommunities(node).map((c) => c.id);
}

export function findNodeInForest(
  forest: MembershipTreeNode[],
  communityId: string
): MembershipTreeNode | null {
  for (const node of forest) {
    if (node.community.id === communityId) return node;
    const found = findNodeInForest(node.children, communityId);
    if (found) return found;
  }
  return null;
}

/** Ancestor community ids from root down to parent of `communityId` (not including self). */
export function getAncestorIdsInForest(
  forest: MembershipTreeNode[],
  communityId: string
): string[] {
  const path: string[] = [];

  const walk = (nodes: MembershipTreeNode[], trail: string[]): boolean => {
    for (const node of nodes) {
      if (node.community.id === communityId) {
        path.push(...trail);
        return true;
      }
      if (walk(node.children, [...trail, node.community.id])) {
        return true;
      }
    }
    return false;
  };

  walk(forest, []);
  return path;
}

/**
 * Ids that must stay expanded while viewing `communityId`:
 * ancestors of the current node, plus the current node when it has children.
 */
export function getForcedExpandedIdsInForest(
  forest: MembershipTreeNode[],
  communityId: string
): string[] {
  const forced = getAncestorIdsInForest(forest, communityId);
  const current = findNodeInForest(forest, communityId);
  if (current && current.children.length > 0) {
    forced.push(current.community.id);
  }
  return forced;
}

/**
 * Parents (aggregator or community with children):
 * - collapsed → expand
 * - expanded → select that parent
 * Leaves → navigate to self.
 * Collapse is only via the chevron in the UI.
 */
export function resolveSmartSwitchTarget(
  node: MembershipTreeNode,
  opts?: { isExpanded?: boolean }
): SmartSwitchResult {
  if (node.children.length > 0) {
    if (!opts?.isExpanded) {
      return { action: 'expand' };
    }
    return { action: 'navigate', communityId: node.community.id };
  }

  return { action: 'navigate', communityId: node.community.id };
}
